import axios from "axios";
import { useState } from "react";
import { ApiConfig } from "../services/api-config";
import { BaseResponse, HttpMethod } from "../services/base-response";
import { useExpired } from "../state/expired-context";
import { useLoading } from "../state/loading-context";
import CookieUtils from "../utils/cookie-utils";

type FetchState = {
  loading: boolean;
  error: string | null;
};

const initialFetchState: FetchState = {
  loading: false,
  error: null,
};

// API Logger Configuration
const API_LOG_CONFIG = {
  enabled:
    window.env.CONFIG_ENVIRONMENT === "beta" ||
    window.env.CONFIG_ENVIRONMENT === "staging",
  showRequestBody: true,
  showResponseData: true,
  maxDataLength: 500,
};

// Color codes for console
const LOG_COLORS = {
  GET: "#61affe",
  POST: "#49cc90",
  PUT: "#fca130",
  DELETE: "#f93e3e",
  INFO: "#9012fe",
  ERROR: "#ff0000",
  SUCCESS: "#00ff00",
};

// Write-only secrets are never printed: Zalo App secret and Checkout key (C-05.1), Pay2S tokens (C-27).
const SECRET_FIELDS = ["zalo_app_secret", "checkout_private_key", "webhook_token", "api_token"];
const SECRET_KEYS = new Set([
  "secret_key",
  "access_key",
  "api_key",
  "checksum_key",
  ...SECRET_FIELDS,
]);

function sanitizeDataForLog(data: unknown): unknown {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) {
    return data.map(sanitizeDataForLog);
  }
  if (typeof data === "object") {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (SECRET_KEYS.has(key)) {
        sanitized[key] = "***";
      } else if (typeof value === "object" && value !== null) {
        sanitized[key] = sanitizeDataForLog(value);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }
  return data;
}
const redactSecrets = (data: unknown) => sanitizeDataForLog(data);

// API Logger utility
const apiLogger = {
  logRequest: (method: HttpMethod, url: string, data?: unknown) => {
    if (!API_LOG_CONFIG.enabled) return;

    const color = LOG_COLORS[method] || LOG_COLORS.INFO;
    const timestamp = new Date().toLocaleTimeString();

    console.groupCollapsed(
      `%c[API ${method}] %c${url} %c@ ${timestamp}`,
      `color: ${color}; font-weight: bold;`,
      "color: #333; font-weight: normal;",
      "color: #999; font-size: 10px;"
    );

    console.log("%c📤 Request URL:", "color: #666; font-weight: bold;", url);

    if (
      API_LOG_CONFIG.showRequestBody &&
      data &&
      Object.keys(data).length > 0
    ) {
      console.log(
        "%c📦 Request Data:",
        "color: #666; font-weight: bold;",
        redactSecrets(data)
      );
    }

    console.groupEnd();
  },

  logResponse: (
    method: HttpMethod,
    url: string,
    status: number,
    data: unknown,
    duration: number
  ) => {
    if (!API_LOG_CONFIG.enabled) return;

    const isSuccess = status >= 200 && status < 300;
    const statusColor = isSuccess ? "#49cc90" : "#f93e3e";
    const timestamp = new Date().toLocaleTimeString();

    console.groupCollapsed(
      `%c[API ${method}] %c${url} %c[${status}] %c${duration}ms %c@ ${timestamp}`,
      `color: ${LOG_COLORS[method]}; font-weight: bold;`,
      "color: #333; font-weight: normal;",
      `color: ${statusColor}; font-weight: bold;`,
      `color: ${duration > 1000 ? "#f93e3e" : "#49cc90"}; font-weight: bold;`,
      "color: #999; font-size: 10px;"
    );

    if (API_LOG_CONFIG.showResponseData) {
      const truncatedData =
        typeof data === "object"
          ? data
          : String(data).slice(0, API_LOG_CONFIG.maxDataLength);
      console.log(
        "%c📥 Response:",
        "color: #666; font-weight: bold;",
        truncatedData
      );
    }

    console.groupEnd();
  },

  logError: (
    method: HttpMethod,
    url: string,
    error: string,
    duration: number
  ) => {
    if (!API_LOG_CONFIG.enabled) return;

    const timestamp = new Date().toLocaleTimeString();

    console.groupCollapsed(
      `%c[API ${method}] %c${url} %c[ERROR] %c${duration}ms %c@ ${timestamp}`,
      `color: ${LOG_COLORS[method]}; font-weight: bold;`,
      "color: #333; font-weight: normal;",
      "color: #f93e3e; font-weight: bold;",
      "color: #f93e3e; font-weight: bold;",
      "color: #999; font-size: 10px;"
    );

    console.log("%c❌ Error:", "color: #f93e3e; font-weight: bold;", error);
    console.groupEnd();
  },
};

interface UseFetchProps {
  endPointUrl?: string;
  showScreenLoading?: boolean;
  projectId?: string;
  apiPublic?: boolean;
}

const useApi = <T>(
  method: HttpMethod,
  showLoading: boolean,
  projectId?: string,
  apiPublic?: boolean
) => {
  const [fetchState, setFetchState] = useState<FetchState>(initialFetchState);
  const [baseResponse, setBaseResponse] = useState<BaseResponse<T>>();
  const { setLoading } = useLoading();
  const { setExpired } = useExpired();
  const finalToken = CookieUtils.getCurrentUser().access_token ?? "";

  const axiosInstance = axios.create({
    baseURL: "",
    headers: {
      "content-type": "application/json",
      ...(finalToken && { Authorization: `Bearer ${finalToken}` }),
      ...(projectId && { "x-svc-id": projectId }),
    },
    responseType: "json",
    timeout: 15000,
  });

  axiosInstance.interceptors.request.use(
    (config) => {
      return config;
    },
    (error: unknown) => {
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: unknown) => {
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  );

  const request = async ({ endPoint = "", data = {} }) => {
    if (showLoading) setLoading(true);
    const finalToken = CookieUtils.getCurrentUser().access_token ?? "";
    const cleanEndPoint = endPoint.replace(/^\/?(api\/)?/, "");
    const fullUrl = apiPublic
      ? `${ApiConfig.API_URL}/${cleanEndPoint}`
      : `${ApiConfig.API_URL}/api/${cleanEndPoint}`;
    const startTime = Date.now();

    // Log request
    apiLogger.logRequest(method, fullUrl, data);

    setFetchState({ loading: true, error: null });
    try {
      const response = await axiosInstance({
        url: fullUrl,
        method: method,
        ...(method === HttpMethod.GET && { params: data }),
        ...((method === HttpMethod.POST ||
          method === HttpMethod.PUT ||
          method === HttpMethod.DELETE) && { data: data }),
      });

      const duration = Date.now() - startTime;

      // Log response
      apiLogger.logResponse(
        method,
        fullUrl,
        response.status,
        response.data,
        duration
      );

      setBaseResponse(response.data);
      setFetchState({ loading: false, error: null });
      if (finalToken) {
        if (response.status === 401 || response.data.status === 401)
          setExpired(true);
      }
      return response.data;
    } catch (error: unknown) {
      const duration = Date.now() - startTime;
      let errorMessage = "";
      if (axios.isCancel(error)) {
        errorMessage =
          error instanceof Error ? error.message : "Request cancelled";
      } else {
        errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";
      }

      // Log error
      apiLogger.logError(method, fullUrl, errorMessage, duration);

      setFetchState({ loading: false, error: errorMessage });
      return (error as any)?.response?.data;
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  return { baseResponse, request, ...fetchState };
};
// Wrapper for cleaner method access
export const useFetchData = {
  Get: <T>({
    projectId = ApiConfig.PROJECT_ID.USER_SERVICE,
    showScreenLoading = false,
    apiPublic = false,
  }: UseFetchProps = {}) => {
    return useApi<T>(HttpMethod.GET, showScreenLoading, projectId, apiPublic);
  },
  Post: <T>({
    projectId = ApiConfig.PROJECT_ID.USER_SERVICE,
    showScreenLoading = false,
  }: UseFetchProps = {}) => {
    return useApi<T>(HttpMethod.POST, showScreenLoading, projectId);
  },
  Put: <T>({
    projectId = ApiConfig.PROJECT_ID.USER_SERVICE,
    showScreenLoading = false,
  }: UseFetchProps = {}) => {
    return useApi<T>(HttpMethod.PUT, showScreenLoading, projectId);
  },
  Delete: <T = void>({
    projectId = ApiConfig.PROJECT_ID.USER_SERVICE,
    showScreenLoading = false,
  }: UseFetchProps = {}) => {
    return useApi<T>(HttpMethod.DELETE, showScreenLoading, projectId);
  },
};
